import { driverInactivityAlert, DriverInactivityAlert } from "./driverActivity";
import type { TowerDriverDetail, TowerListedShipment, TowerShipment } from "../../../types/controlTower";

// Alertas de la campanita: choferes con alerta de inactividad y envíos del
// depósito que quedaron "En camino". Solo para hoy: en otro día no hay
// inactividad que medir. "De hoy" es lo que no se arrastra de días anteriores
// (el backend lo marca con carryover).

export type DriverAlertRow = {
  driver: TowerDriverDetail;
  alert: NonNullable<DriverInactivityAlert>;
  flexInTransit: number;
  localities: { name: string; count: number }[];
};

const RANK = { critical: 0, warning: 1, noDeliveries: 2 } as const;

export const isToday = (s: Pick<TowerShipment, "carryover">) => !s.carryover;

// Las localidades donde más envíos tiene el chofer hoy (para ubicarlo rápido).
export function topLocalities(driver: TowerDriverDetail, limit = 3): { name: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const s of driver.shipments) {
    const name = s.locality?.trim();
    if (name) counts.set(name, (counts.get(name) || 0) + 1);
  }
  return [...counts]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "es"))
    .slice(0, limit);
}

// La alerta mira los Flex en camino de hoy (driver.counts): un chofer que solo
// arrastra envíos viejos no alerta.
export function driverAlertRows(drivers: TowerDriverDetail[], nowMs: number): DriverAlertRow[] {
  const rows: DriverAlertRow[] = [];
  for (const driver of drivers) {
    const alert = driverInactivityAlert(driver, driver, nowMs);
    if (alert)
      rows.push({ driver, alert, flexInTransit: driver.counts.flexInTransit, localities: topLocalities(driver) });
  }
  const idle = (row: DriverAlertRow) => ("idleMinutes" in row.alert ? row.alert.idleMinutes : 0);
  return rows.sort(
    (a, b) =>
      RANK[a.alert.level] - RANK[b.alert.level] ||
      idle(b) - idle(a) ||
      a.driver.name.localeCompare(b.driver.name, "es"),
  );
}

// Orden de las zonas en el panel de alertas, como en Monitoreo.
export const ALERT_ZONE_ORDER = ["NORTE", "SUR", "CABA", "OESTE"];

// Zona bajo la que se lista la alerta de un chofer: donde tiene más Flex en
// camino hoy (lo que dispara la alerta) y, si no tiene, donde tiene más envíos
// de hoy. Sin zona conocida, "Sin zona".
export function alertZone(driver: Pick<TowerDriverDetail, "shipments">): string {
  const pick = (list: { zone: string }[]) => {
    const counts = new Map<string, number>();
    for (const s of list) if (ALERT_ZONE_ORDER.includes(s.zone)) counts.set(s.zone, (counts.get(s.zone) || 0) + 1);
    let best: string | null = null;
    for (const zone of ALERT_ZONE_ORDER) if ((counts.get(zone) || 0) > (best ? counts.get(best) || 0 : 0)) best = zone;
    return best;
  };
  const shipments = driver.shipments.filter(isToday);
  const flex = shipments.filter((s) => s.status === "inTransit" && s.shippingUniverse === "FLEX");
  return pick(flex) || pick(shipments) || "Sin zona";
}

// Envíos del depósito que siguen "En camino" hoy: no deberían estar en reparto.
export function depotInTransit(depot: TowerListedShipment[]): TowerListedShipment[] {
  return depot.filter((s) => s.status === "inTransit" && isToday(s));
}
